import React, { useState, useEffect } from 'react';
import { db } from "../firebase";
import { 
  collection, 
  getDocs, 
  addDoc, 
  deleteDoc, 
  doc 
} from "firebase/firestore";
import { 
  Container, Typography, Box, TextField, Button, 
  Paper, CircularProgress, Alert, Snackbar,
  Table, TableBody, TableCell, TableContainer, 
  TableHead, TableRow, IconButton, Divider, Chip
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import DeleteIcon from '@mui/icons-material/Delete';
import EmailIcon from '@mui/icons-material/Email';
import PeopleIcon from '@mui/icons-material/People';
import { getFunctions, httpsCallable } from "firebase/functions";

const NewsLetters = () => {
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [subscribers, setSubscribers] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [status, setStatus] = useState({ open: false, msg: "", severity: "success" });

  // 1. Fetch subscribers using ID Mapping
  const fetchSubscribers = async () => {
    setFetching(true);
    try {
      const querySnapshot = await getDocs(collection(db, "newsletters"));
      const list = querySnapshot.docs.map(doc => ({
        uid: doc.id, // This is the mapped Firebase UID
        ...doc.data()
      }));
      setSubscribers(list);
    } catch (error) {
      console.error("Error fetching subscribers:", error);
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    fetchSubscribers();
  }, []);

  // 2. Broadcast Logic
  const handleSendNewsletter = async (e) => {
  e.preventDefault();
  if (subscribers.length === 0) return;

  setLoading(true);
  const functions = getFunctions();
  const sendNewsletter = httpsCallable(functions, 'sendNewsletter');

  try {
    const result = await sendNewsletter({
      subject: subject,
      message: message,
      subscribers: subscribers // This contains {uid, email, fullName}
    });

    if (result.data.success) {
      setStatus({ 
        open: true, 
        msg: `Successfully sent to ${result.data.count} subscribers!`, 
        severity: "success" 
      });
      setSubject("");
      setMessage("");
    }
  } catch (error) {
    console.error(error);
    setStatus({ open: true, msg: "Error: " + error.message, severity: "error" });
  } finally {
    setLoading(false);
  }
};

  // 3. Delete Subscriber Logic
  const handleDeleteSubscriber = async (uid) => {
    if (window.confirm("Are you sure you want to remove this subscriber?")) {
      try {
        await deleteDoc(doc(db, "newsletters", uid));
        setSubscribers(subscribers.filter(s => s.uid !== uid));
        setStatus({ open: true, msg: "Subscriber removed.", severity: "info" });
      } catch (error) {
        setStatus({ open: true, msg: "Error removing subscriber.", severity: "error" });
      }
    }
  };

  return (
    <Container sx={{ py: 8, mt: 5 }}>
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <EmailIcon color="primary" sx={{ fontSize: 40 }} />
        <Typography variant="h4" sx={{ fontWeight: 700 }}>Newsletter Management</Typography>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { md: '1fr 1.5fr' }, gap: 4 }}>
        
        {/* Left Column: Broadcast Form */}
        <Paper sx={{ p: 4, borderRadius: 4, height: 'fit-content' }} elevation={3}>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <SendIcon fontSize="small" /> New Broadcast
          </Typography>
          <form onSubmit={handleSendNewsletter}>
            <TextField
              fullWidth
              label="Email Subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              required
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Message Body"
              multiline
              rows={8}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              required
              sx={{ mb: 3 }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading || subscribers.length === 0}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
              sx={{ 
                bgcolor: '#2ecc71', 
                py: 1.5, 
                borderRadius: 2,
                '&:hover': { bgcolor: '#27ae60' }
              }}
            >
              {loading ? "Sending..." : "Send to All Subscribers"}
            </Button>
          </form>
        </Paper>

        {/* Right Column: Subscriber List */}
        <Paper sx={{ p: 4, borderRadius: 4 }} elevation={3}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PeopleIcon fontSize="small" /> Subscriber List
            </Typography>
            <Chip label={`${subscribers.length} Subscribed`} color="success" variant="outlined" />
          </Box>
          
          <Divider sx={{ mb: 2 }} />

          <TableContainer sx={{ maxHeight: 500 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell><b>Name</b></TableCell>
                  <TableCell><b>Email</b></TableCell>
                  <TableCell align="right"><b>Action</b></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {fetching ? (
                  <TableRow><TableCell colSpan={3} align="center"><CircularProgress sx={{ my: 2 }} /></TableCell></TableRow>
                ) : subscribers.length > 0 ? (
                  subscribers.map((sub) => (
                    <TableRow key={sub.uid} hover>
                      <TableCell>{sub.fullName}</TableCell>
                      <TableCell>{sub.email}</TableCell>
                      <TableCell align="right">
                        <IconButton color="error" onClick={() => handleDeleteSubscriber(sub.uid)}>
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow><TableCell colSpan={3} align="center">No subscribers found.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>

      <Snackbar 
        open={status.open} 
        autoHideDuration={4000} 
        onClose={() => setStatus({ ...status, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={status.severity}>{status.msg}</Alert>
      </Snackbar>
    </Container>
  );
};

export default NewsLetters;